/**
 * Módulo Integración IT · Página admin.
 *
 * Permite crear checklists para onboarding de centros adquiridos,
 * compartir URL pública protegida por contraseña con el equipo del nuevo centro,
 * y generar enlaces específicos por perfil (IT, Jurídico, Finanzas, Compliance…).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import {
  CheckCircle2, Plus, Loader2, Copy, ExternalLink, X, Lock,
  AlertTriangle, FileCheck, Trash2, Eye, Key, FileText,
  Share2, Users2, ChevronDown, ChevronRight, Link2, ShieldCheck,
  LayoutDashboard, Settings2, Edit3, EyeOff, BarChart3,
  Mail, Send, Download, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const ESTADO = {
  pendiente: { l: "Pendiente",  c: "bg-gray-100 text-gray-600" },
  en_curso:  { l: "En curso",   c: "bg-blue-100 text-blue-700" },
  bloqueado: { l: "Bloqueado",  c: "bg-rose-100 text-rose-700" },
  ok:        { l: "OK",         c: "bg-emerald-100 text-emerald-700" },
  na:        { l: "N/A",        c: "bg-slate-100 text-slate-500" },
};
const CRIT = {
  baja:     "bg-gray-100 text-gray-600",
  media:    "bg-amber-100 text-amber-700",
  alta:     "bg-orange-100 text-orange-700",
  crítica:  "bg-rose-100 text-rose-700",
};

const PROFILE_OPTIONS = [
  "IT", "Jurídico", "Finanzas", "Compliance", "Administración",
  "Operaciones", "Dirección", "RRHH", "Otro",
];

const PROFILE_COLORS = {
  IT:              "bg-blue-100 text-blue-700",
  Jurídico:        "bg-purple-100 text-purple-700",
  Finanzas:        "bg-amber-100 text-amber-700",
  Compliance:      "bg-emerald-100 text-emerald-700",
  Administración:  "bg-gray-100 text-gray-700",
  Operaciones:     "bg-orange-100 text-orange-700",
  Dirección:       "bg-rose-100 text-rose-700",
  RRHH:            "bg-teal-100 text-teal-700",
  Otro:            "bg-slate-100 text-slate-600",
};

export default function IntegrationIT() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);  // checklist para share links
  const [showDashboard, setShowDashboard] = useState(true);
  const [showFieldsEditor, setShowFieldsEditor] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [delta, setDelta] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/integration/checklists`);
    setList(r.data);
  };
  const loadDash = async () => {
    setDashLoading(true);
    try {
      const [d, dt] = await Promise.all([
        axios.get(`${API}/integration/dashboard`),
        axios.get(`${API}/integration/dashboard/delta`).catch(() => ({ data: { has_previous: false } })),
      ]);
      setDash(d.data);
      setDelta(dt.data);
    } catch (e) {
      toast.error("No se pudo cargar el dashboard");
    } finally {
      setDashLoading(false);
    }
  };
  useEffect(() => { load(); loadDash(); }, []);

  const del = async (cid) => {
    if (!window.confirm("¿Eliminar checklist completo (irreversible)?")) return;
    await axios.delete(`${API}/integration/checklists/${cid}`);
    toast.success("Eliminado"); load();
  };

  const openView = async (cid) => {
    const r = await axios.get(`${API}/integration/checklists/${cid}`);
    setViewing(r.data);
  };

  const downloadPdf = async (cid, slug) => {
    try {
      const r = await axios.get(`${API}/integration/checklists/${cid}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug || "checklist"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("Error generando PDF: " + (e.response?.data?.detail || e.message));
    }
  };

  if (!list) return <div className="text-xs text-gray-500 p-4"><Loader2 className="animate-spin inline mr-1" size={12}/>Cargando…</div>;

  return (
    <div className="p-4 space-y-4 w-full" data-testid="integration-it-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileCheck size={20} className="text-[#007934]"/> Integración IT · Onboarding de centros
          </h1>
          <p className="text-xs text-gray-500">
            Checklists para la absorción TIC de centros recién adquiridos.
            Comparte enlace por perfil (IT, Jurídico, Finanzas…) con contraseña y vista personalizada.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1"
          data-testid="intit-new-btn">
          <Plus size={13}/> Nuevo checklist
        </button>
      </div>

      {/* Dashboard + Editor controls */}
      <div className="flex items-center justify-between flex-wrap gap-2 -mt-2">
        <div className="flex items-center gap-1.5 text-xs">
          <button
            onClick={() => setShowDashboard(s => !s)}
            className="px-2 py-1 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 flex items-center gap-1"
            data-testid="intit-toggle-dashboard">
            <LayoutDashboard size={11}/> {showDashboard ? "Ocultar" : "Mostrar"} dashboard
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowFieldsEditor(true)}
              className="px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 flex items-center gap-1 font-semibold"
              data-testid="intit-fields-editor-btn">
              <Settings2 size={11}/> Editor de campos globales
            </button>
          )}
          <button
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/integration/dashboard/export-pdf`, { responseType: "blob" });
                const blob = new Blob([r.data], { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `integracion-it-snapshot-${new Date().toISOString().slice(0,7)}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                toast.success("PDF generado");
              } catch (e) {
                toast.error("Error generando PDF: " + (e.response?.data?.detail || e.message));
              }
            }}
            className="px-2 py-1 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 flex items-center gap-1 font-semibold"
            data-testid="intit-dashboard-pdf-btn"
            title="Descargar snapshot PDF del Comité">
            <Download size={11}/> PDF Comité
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowEmailSettings(true)}
              className="px-2 py-1 rounded-lg border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 flex items-center gap-1 font-semibold"
              data-testid="intit-email-settings-btn"
              title="Configurar envío mensual al Comité de Integración">
              <Mail size={11}/> Reporte mensual
            </button>
          )}
        </div>
        {dash && (
          <div className="text-[10px] text-gray-500">
            {dash.kpis.checklists_count} checklists · {dash.kpis.total_items} items totales
          </div>
        )}
      </div>

      {showDashboard && (
        <DashboardOverview dash={dash} delta={delta} loading={dashLoading} onRefresh={loadDash} />
      )}

      {list.length === 0 && (
        <div className="text-center text-gray-500 bg-white rounded-xl border p-10">
          <FileCheck size={32} className="mx-auto text-gray-300 mb-2"/>
          <p className="text-sm font-semibold">Sin checklists aún</p>
          <p className="text-xs mt-1">Crea el primero para arrancar la integración IT del nuevo centro.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(c => (
          <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`intit-card-${c.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-gray-900 truncate">{c.name}</h3>
                <p className="text-[10px] text-gray-500 truncate">{c.acquired_company}</p>
              </div>
              <button onClick={()=>del(c.id)} className="text-gray-300 hover:text-rose-500 p-1" title="Eliminar">
                <Trash2 size={12}/>
              </button>
            </div>

            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="text-gray-600">{c.done} de {c.total_items} completados</span>
                <span className="font-bold text-[#007934]">{c.progress}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#007934] rounded-full" style={{ width: `${c.progress}%` }}/>
              </div>
              {c.blocked > 0 && (
                <p className="text-[10px] text-rose-600 mt-1 flex items-center gap-0.5">
                  <AlertTriangle size={9}/> {c.blocked} bloqueados
                </p>
              )}
            </div>

            {/* Perfil badges */}
            {c.share_links_count > 0 && (
              <div className="flex items-center gap-1 mb-2 flex-wrap">
                <ShieldCheck size={10} className="text-emerald-600 flex-shrink-0"/>
                <span className="text-[10px] text-emerald-700 font-medium">{c.share_links_count} enlace{c.share_links_count>1?"s":""} de perfil activo{c.share_links_count>1?"s":""}</span>
              </div>
            )}

            <div className="flex gap-1 flex-wrap">
              <button
                onClick={()=>openView(c.id)}
                className="flex-1 text-xs bg-gray-50 hover:bg-gray-100 text-gray-700 px-2 py-1 rounded flex items-center justify-center gap-1"
                data-testid={`intit-open-${c.id}`}>
                <Eye size={11}/> Ver
              </button>
              <button
                onClick={() => setShareTarget(c)}
                className="text-xs bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-2 py-1 rounded flex items-center gap-1 font-semibold"
                data-testid={`intit-profiles-${c.id}`}
                title="Gestionar enlaces por perfil">
                <Users2 size={11}/> Perfiles
              </button>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/integration/${c.slug}`;
                  navigator.clipboard.writeText(url);
                  toast.success("URL principal copiada");
                }}
                className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1"
                data-testid={`intit-share-${c.id}`}
                title="Copiar URL pública principal">
                <Copy size={11}/>
              </button>
              <a
                href={`/integration/${c.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-2 py-1 rounded flex items-center gap-1"
                title="Abrir vista pública">
                <ExternalLink size={11}/>
              </a>
              <button
                onClick={() => downloadPdf(c.id, c.slug)}
                className="text-xs bg-rose-50 hover:bg-rose-100 text-rose-700 px-2 py-1 rounded flex items-center gap-1"
                data-testid={`intit-pdf-${c.id}`}
                title="Descargar PDF">
                <FileText size={11}/>
              </button>
            </div>
            <p className="text-[9px] text-gray-400 mt-2 font-mono truncate">{c.slug}</p>
          </div>
        ))}
      </div>

      {showForm && <NewChecklistModal onClose={()=>{setShowForm(false); load(); loadDash();}}/>}
      {viewing  && <ChecklistDetailModal data={viewing} onClose={()=>{setViewing(null); load(); loadDash();}} reload={async()=>{const r=await axios.get(`${API}/integration/checklists/${viewing.id}`);setViewing(r.data);}}/>}
      {shareTarget && <ShareLinksModal checklist={shareTarget} onClose={()=>{setShareTarget(null); load();}}/>}
      {showFieldsEditor && <CustomFieldsEditor onClose={()=>{setShowFieldsEditor(false); loadDash();}}/>}
      {showEmailSettings && <EmailSettingsModal onClose={()=>setShowEmailSettings(false)}/>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL: Gestión de enlaces por perfil
// ════════════════════════════════════════════════════════════════════════════
function ShareLinksModal({ checklist, onClose }) {
  const [links, setLinks] = useState(null);
  const [allCats, setAllCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    profile: "IT", label: "", password: "", visible_categories: [],
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await axios.get(`${API}/integration/checklists/${checklist.id}/share-links`);
    setLinks(r.data.share_links || []);
    // Cargar categorías del checklist para el selector
    const det = await axios.get(`${API}/integration/checklists/${checklist.id}`);
    const cats = [...new Set((det.data.items || []).map(i => i.category).filter(Boolean))];
    setAllCats(cats);
  };

  useEffect(() => { load(); }, []);

  const deleteLink = async (lid) => {
    if (!window.confirm("¿Eliminar este enlace de perfil?")) return;
    await axios.delete(`${API}/integration/checklists/${checklist.id}/share-links/${lid}`);
    toast.success("Enlace eliminado"); load();
  };

  const createLink = async () => {
    if (!form.label || !form.password || form.password.length < 6) {
      toast.error("Etiqueta y contraseña (≥6 chars) requeridas"); return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/integration/checklists/${checklist.id}/share-links`, form);
      toast.success(`Enlace para "${form.profile}" creado`);
      const url = `${window.location.origin}/integration/${checklist.slug}${r.data.public_url_suffix}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.info("URL copiada al portapapeles");
      } catch {
        // Clipboard not available in some contexts — silent fail
      }
      setShowForm(false);
      setForm({ profile: "IT", label: "", password: "", visible_categories: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear enlace");
    } finally { setSaving(false); }
  };

  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      visible_categories: f.visible_categories.includes(cat)
        ? f.visible_categories.filter(c => c !== cat)
        : [...f.visible_categories, cat],
    }));
  };

  const buildUrl = (link) =>
    `${window.location.origin}/integration/${checklist.slug}${link.public_url_suffix}`;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#007934] to-[#00572a] text-white">
          <div>
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Users2 size={15}/> Compartir por perfil
            </h3>
            <p className="text-xs opacity-80">{checklist.name}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
            <p className="font-semibold mb-1">¿Cómo funciona?</p>
            <p>Cada enlace de perfil tiene su propia contraseña y muestra solo las categorías que selecciones.
            Envía el enlace al destinatario junto con su contraseña — no verán otras secciones del checklist.</p>
          </div>

          {/* Lista de enlaces existentes */}
          {links === null ? (
            <div className="text-center py-4"><Loader2 className="animate-spin inline" size={16}/></div>
          ) : links.length === 0 ? (
            <div className="text-center text-gray-400 py-6 text-xs">
              <Link2 size={28} className="mx-auto mb-2 text-gray-200"/>
              Sin enlaces de perfil creados aún.
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide">
                {links.length} enlace{links.length>1?"s":""} activo{links.length>1?"s":""}
              </p>
              {links.map(link => (
                <div key={link.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50/50" data-testid={`share-link-${link.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${PROFILE_COLORS[link.profile] || "bg-gray-100 text-gray-700"}`}>
                        {link.profile}
                      </span>
                      <span className="text-xs font-semibold text-gray-800">{link.label}</span>
                    </div>
                    <button onClick={()=>deleteLink(link.id)} className="text-gray-300 hover:text-rose-500 flex-shrink-0" title="Eliminar enlace">
                      <Trash2 size={12}/>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {link.visible_categories?.length === 0 ? (
                      <span className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">Todas las categorías</span>
                    ) : link.visible_categories?.map(cat => (
                      <span key={cat} className="text-[10px] bg-white border border-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{cat}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-[9px] text-gray-400 bg-white border border-gray-100 rounded px-2 py-0.5 flex-1 truncate">
                      /integration/{checklist.slug}?link={link.id}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(buildUrl(link));
                        toast.success(`URL "${link.profile}" copiada`);
                      }}
                      className="text-xs bg-white border border-gray-200 hover:border-[#007934] hover:text-[#007934] px-2 py-0.5 rounded flex items-center gap-1 flex-shrink-0"
                      data-testid={`copy-link-${link.id}`}>
                      <Copy size={10}/> Copiar URL
                    </button>
                    <a
                      href={buildUrl(link)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded flex items-center gap-1 flex-shrink-0">
                      <ExternalLink size={10}/> Abrir
                    </a>
                  </div>
                  <p className="text-[9px] text-gray-400 mt-1">Creado por {link.created_by} · {link.created_at?.slice(0,10)}</p>
                </div>
              ))}
            </div>
          )}

          {/* Formulario nuevo enlace */}
          {showForm ? (
            <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50/50 space-y-3">
              <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1">
                <Plus size={12}/> Nuevo enlace de perfil
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="block">
                  <span className="text-[10px] uppercase text-gray-500 font-semibold">Perfil *</span>
                  <select
                    value={form.profile}
                    onChange={e=>setForm({...form, profile: e.target.value})}
                    className="w-full border rounded px-2 py-1.5 mt-0.5 bg-white text-xs"
                    data-testid="share-profile-select">
                    {PROFILE_OPTIONS.map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase text-gray-500 font-semibold">Etiqueta *</span>
                  <input
                    value={form.label}
                    onChange={e=>setForm({...form, label: e.target.value})}
                    placeholder="Ej: Equipo Jurídico Madrid"
                    className="w-full border rounded px-2 py-1.5 mt-0.5 text-xs"
                    data-testid="share-label-input"/>
                </label>
                <label className="block col-span-2">
                  <span className="text-[10px] uppercase text-gray-500 font-semibold">Contraseña específica * <span className="text-gray-400 normal-case">(≥6 caracteres)</span></span>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e=>setForm({...form, password: e.target.value})}
                    placeholder="Contraseña para este perfil"
                    className="w-full border rounded px-2 py-1.5 mt-0.5 font-mono text-xs"
                    data-testid="share-password-input"/>
                </label>
              </div>
              {/* Categorías visibles */}
              <div>
                <p className="text-[10px] uppercase text-gray-500 font-semibold mb-1.5">
                  Categorías visibles <span className="text-gray-400 normal-case font-normal">(vacío = todas)</span>
                </p>
                <div className="space-y-1">
                  {allCats.map(cat => (
                    <label key={cat} className="flex items-center gap-2 cursor-pointer text-xs text-gray-700 hover:text-gray-900">
                      <input
                        type="checkbox"
                        checked={form.visible_categories.includes(cat)}
                        onChange={()=>toggleCat(cat)}
                        className="rounded border-gray-300 text-[#007934] focus:ring-[#007934]"
                        data-testid={`share-cat-${cat}`}/>
                      <span>{cat}</span>
                    </label>
                  ))}
                </div>
                {form.visible_categories.length === 0 && (
                  <p className="text-[10px] text-blue-600 mt-1">Se mostrarán todas las categorías del checklist.</p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-emerald-100">
                <button onClick={()=>setShowForm(false)} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
                <button
                  onClick={createLink}
                  disabled={saving}
                  className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded flex items-center gap-1"
                  data-testid="share-create-btn">
                  {saving ? <><Loader2 size={11} className="animate-spin"/>Creando…</> : <><Plus size={11}/> Crear enlace</>}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={()=>setShowForm(true)}
              className="w-full border-2 border-dashed border-emerald-200 rounded-xl py-3 text-xs text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-1.5 font-semibold"
              data-testid="add-share-link-btn">
              <Plus size={13}/> Crear nuevo enlace de perfil
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-3 flex justify-end">
          <button onClick={onClose} className="text-xs border px-4 py-1.5 rounded hover:bg-gray-50">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL: Crear nuevo checklist — wizard 2 pasos
// ════════════════════════════════════════════════════════════════════════════
const DEPT_ICONS = {
  IT:          "💻",
  "RR.HH.":   "👥",
  Finanzas:    "💰",
  Jurídico:    "⚖️",
  Compliance:  "🛡",
  Producción:  "🏭",
};

const DEPT_COLORS = {
  IT:          { border: "border-blue-300",   bg: "bg-blue-50",   text: "text-blue-700",   badge: "bg-blue-100" },
  "RR.HH.":   { border: "border-purple-300",  bg: "bg-purple-50", text: "text-purple-700", badge: "bg-purple-100" },
  Finanzas:    { border: "border-amber-300",   bg: "bg-amber-50",  text: "text-amber-700",  badge: "bg-amber-100" },
  Jurídico:    { border: "border-violet-300",  bg: "bg-violet-50", text: "text-violet-700", badge: "bg-violet-100" },
  Compliance:  { border: "border-emerald-300", bg: "bg-emerald-50",text: "text-emerald-700",badge: "bg-emerald-100" },
  Producción:  { border: "border-orange-300",  bg: "bg-orange-50", text: "text-orange-700", badge: "bg-orange-100" },
};

function NewChecklistModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [f, setF] = useState({ name: "", acquired_company: "", password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/integration/templates`).then(r => setTemplates(r.data));
  }, []);

  const toggle = (tid) =>
    setSelected(s => s.includes(tid) ? s.filter(x => x !== tid) : [...s, tid]);

  const totalItems = templates
    .filter(t => selected.includes(t.id))
    .reduce((acc, t) => acc + t.items_count, 0);

  const save = async () => {
    if (!f.name || !f.password || f.password.length < 6) {
      toast.error("Nombre y contraseña (≥6 chars) requeridos"); return;
    }
    if (selected.length === 0) {
      toast.error("Selecciona al menos un departamento"); return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/integration/checklists`, { ...f, template_ids: selected });
      toast.success(`Checklist creado con ${r.data.items.length} tareas`);
      navigator.clipboard.writeText(`${window.location.origin}/integration/${r.data.slug}`);
      toast.info("URL copiada al portapapeles");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="bg-gradient-to-r from-[#007934] to-[#00572a] p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] opacity-80 uppercase tracking-wide">
                Paso {step} de 2
              </p>
              <h3 className="text-sm font-bold">
                {step === 1 ? "Selecciona los departamentos" : "Datos del checklist"}
              </h3>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white"><X size={18}/></button>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-white/20 rounded-full">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: step === 1 ? "50%" : "100%" }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* ── PASO 1: Selector de templates ── */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">
                Elige los departamentos involucrados en la integración. Sus tareas se combinarán en un único checklist que podrás personalizar después.
              </p>

              <div className="grid grid-cols-2 gap-2.5">
                {templates.map(t => {
                  const isSelected = selected.includes(t.id);
                  const colors = DEPT_COLORS[t.dept] || DEPT_COLORS["IT"];
                  return (
                    <button
                      key={t.id}
                      onClick={() => toggle(t.id)}
                      data-testid={`template-${t.id}`}
                      className={`text-left border-2 rounded-xl p-3 transition-all ${
                        isSelected
                          ? `${colors.border} ${colors.bg} shadow-sm`
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-lg leading-none">{DEPT_ICONS[t.dept] || "📋"}</span>
                          <span className={`text-xs font-bold ${isSelected ? colors.text : "text-gray-800"}`}>
                            {t.name}
                          </span>
                        </div>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? `${colors.border.replace("border-","border-")} bg-white` : "border-gray-300"
                        }`}>
                          {isSelected && <CheckCircle2 size={12} className={colors.text}/>}
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-relaxed mb-2">{t.description}</p>
                      <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${isSelected ? `${colors.badge} ${colors.text}` : "bg-gray-100 text-gray-500"}`}>
                        {t.items_count} tareas
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Resumen selección */}
              {selected.length > 0 && (
                <div className="bg-[#007934]/5 border border-[#007934]/20 rounded-xl p-3 text-xs">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-semibold text-[#007934]">Seleccionados: {selected.length} departamento{selected.length > 1 ? "s" : ""}</span>
                    <span className="font-bold text-gray-700">{totalItems} tareas en total</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {templates.filter(t => selected.includes(t.id)).map(t => {
                      const colors = DEPT_COLORS[t.dept] || DEPT_COLORS["IT"];
                      return (
                        <span key={t.id} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors.badge} ${colors.text}`}>
                          {DEPT_ICONS[t.dept]} {t.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── PASO 2: Datos del checklist ── */}
          {step === 2 && (
            <div className="space-y-3 text-xs">
              {/* Resumen templates seleccionados */}
              <div className="flex flex-wrap gap-1 bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                <span className="text-[10px] text-gray-500 font-semibold w-full mb-1">Plantillas seleccionadas:</span>
                {templates.filter(t => selected.includes(t.id)).map(t => {
                  const colors = DEPT_COLORS[t.dept] || DEPT_COLORS["IT"];
                  return (
                    <span key={t.id} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors.badge} ${colors.text}`}>
                      {DEPT_ICONS[t.dept]} {t.name} · {t.items_count} tareas
                    </span>
                  );
                })}
                <span className="text-[10px] text-gray-500 w-full mt-1">
                  Total: <strong>{totalItems} tareas precargadas</strong> (podrás añadir, editar o eliminar después)
                </span>
              </div>

              <label className="block">
                <span className="text-[10px] uppercase text-gray-500 font-semibold">Nombre del checklist *</span>
                <input
                  autoFocus
                  value={f.name}
                  onChange={e => setF({...f, name: e.target.value})}
                  placeholder="Ej: FIRMA · Integración M&A 2026"
                  className="w-full border rounded-lg px-3 py-2 mt-0.5 text-xs"
                  data-testid="intit-new-name"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase text-gray-500 font-semibold">Empresa adquirida</span>
                <input
                  value={f.acquired_company}
                  onChange={e => setF({...f, acquired_company: e.target.value})}
                  placeholder="FIRMA (Grupo Barceló)"
                  className="w-full border rounded-lg px-3 py-2 mt-0.5 text-xs"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase text-gray-500 font-semibold">
                  Contraseña principal * <span className="font-normal text-gray-400">(acceso global · mín. 6 chars)</span>
                </span>
                <input
                  type="password"
                  value={f.password}
                  onChange={e => setF({...f, password: e.target.value})}
                  placeholder="Contraseña de acceso global"
                  className="w-full border rounded-lg px-3 py-2 mt-0.5 text-xs font-mono"
                  data-testid="intit-new-pw"
                />
              </label>
              <p className="text-[10px] text-blue-700 bg-blue-50 border border-blue-100 rounded-lg p-2.5 leading-relaxed">
                Tras crear el checklist puedes generar <strong>enlaces específicos por perfil</strong> (IT, Jurídico, Finanzas…)
                con contraseñas distintas y solo las secciones relevantes para cada equipo.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-3 flex items-center justify-between gap-2">
          {step === 1 ? (
            <>
              <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button
                onClick={() => setStep(2)}
                disabled={selected.length === 0}
                className="text-xs bg-[#007934] text-white px-4 py-1.5 rounded-lg disabled:opacity-40 flex items-center gap-1 font-semibold"
                data-testid="intit-next-step"
              >
                Siguiente → Configurar
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="text-xs border px-3 py-1.5 rounded-lg hover:bg-gray-50 flex items-center gap-1">
                ← Volver
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="text-xs bg-[#007934] text-white px-4 py-1.5 rounded-lg flex items-center gap-1 font-semibold"
                data-testid="intit-new-save"
              >
                {saving ? <><Loader2 size={11} className="animate-spin"/> Creando…</> : `Crear checklist (${totalItems} tareas)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL: Detalle checklist
// ════════════════════════════════════════════════════════════════════════════
function ChecklistDetailModal({ data, onClose, reload }) {
  const [filter, setFilter] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("");
  const [editing, setEditing] = useState(null);

  const items = (data.items || []).filter(i => {
    if (estadoFilter && i.estado !== estadoFilter) return false;
    if (filter) {
      const f = filter.toLowerCase();
      if (!i.title?.toLowerCase().includes(f) && !i.category?.toLowerCase().includes(f)
          && !i.subcategory?.toLowerCase().includes(f)) return false;
    }
    return true;
  });

  const byCategory = {};
  items.forEach(i => { (byCategory[i.category] ||= []).push(i); });

  const updateItem = async (iid, patch) => {
    await axios.put(`${API}/integration/checklists/${data.id}/items/${iid}`, patch);
    toast.success("Actualizado");
    reload();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#007934] to-[#00572a] text-white">
          <div>
            <h3 className="text-base font-bold">{data.name}</h3>
            <p className="text-xs opacity-90">{data.acquired_company} · {(data.items||[]).length} items</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const url = `${window.location.origin}/integration/${data.slug}`;
                navigator.clipboard.writeText(url);
                toast.success("URL copiada");
              }}
              className="bg-white/15 hover:bg-white/25 text-white text-xs px-3 py-1.5 rounded flex items-center gap-1">
              <Copy size={11}/> Copiar URL pública
            </button>
            <button onClick={onClose} className="text-white/70"><X size={18}/></button>
          </div>
        </div>

        <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-2 flex-wrap">
          <input
            placeholder="Buscar…"
            value={filter}
            onChange={e=>setFilter(e.target.value)}
            className="flex-1 min-w-[200px] border rounded px-2 py-1 text-xs"
          />
          <select value={estadoFilter} onChange={e=>setEstadoFilter(e.target.value)} className="text-xs border rounded px-2 py-1 bg-white">
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO).map(([v, e]) => <option key={v} value={v}>{e.l}</option>)}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {Object.entries(byCategory).map(([cat, its]) => (
            <div key={cat}>
              <h4 className="text-sm font-bold text-[#007934] mb-2 sticky top-0 bg-white pb-1 border-b">
                {cat} <span className="text-[10px] text-gray-500 font-normal">· {its.length} items</span>
              </h4>
              <div className="space-y-1.5">
                {its.map(i => (
                  <div key={i.id} className="border border-gray-200 rounded p-2.5 hover:bg-gray-50 cursor-pointer" onClick={()=>setEditing(i)} data-testid={`intit-item-${i.id}`}>
                    <div className="flex items-start gap-2">
                      <select
                        value={i.estado || "pendiente"}
                        onClick={e=>e.stopPropagation()}
                        onChange={e=>updateItem(i.id, {estado: e.target.value})}
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded border-0 ${ESTADO[i.estado]?.c || "bg-gray-100"}`}>
                        {Object.entries(ESTADO).map(([v, e]) => <option key={v} value={v}>{e.l}</option>)}
                      </select>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${CRIT[i.criticidad] || "bg-gray-100"}`}>{i.criticidad}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900">{i.title}</p>
                        {i.subcategory && <p className="text-[10px] text-gray-500">{i.subcategory}</p>}
                        {i.description && <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">{i.description}</p>}
                      </div>
                      <div className="text-[9px] text-right text-gray-500 whitespace-nowrap">
                        {i.owner_tecnico && <div>👤 {i.owner_tecnico}</div>}
                        {i.owner_funcional && <div>🏢 {i.owner_funcional}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <ItemEditModal
            item={editing}
            onClose={()=>setEditing(null)}
            onSave={async (patch) => { await updateItem(editing.id, patch); setEditing(null); }}
          />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MODAL: Editar item
// ════════════════════════════════════════════════════════════════════════════
function ItemEditModal({ item, onClose, onSave }) {
  const [f, setF] = useState({ ...item });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const patch = {};
    ["estado", "criticidad", "notas", "owner_funcional", "owner_tecnico",
     "proveedor", "contrato", "usuarios", "licencias", "ubicacion",
     "modalidad_alojamiento", "integraciones", "accesos_admin",
     "backups", "plan_continuidad"].forEach(k => { if (f[k] !== item[k]) patch[k] = f[k]; });
    try { await onSave(patch); } catch {}
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[88vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
        <div className="p-4 border-b sticky top-0 bg-white flex justify-between items-center">
          <div>
            <p className="text-[10px] text-gray-500">{item.category} · {item.subcategory}</p>
            <h3 className="text-sm font-bold text-gray-900">{item.title}</h3>
          </div>
          <button onClick={onClose}><X size={16}/></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3 text-xs">
          <L label="Estado">
            <select value={f.estado||"pendiente"} onChange={e=>setF({...f,estado:e.target.value})} className="w-full border rounded px-2 py-1 bg-white">
              {Object.entries(ESTADO).map(([v, e]) => <option key={v} value={v}>{e.l}</option>)}
            </select>
          </L>
          <L label="Criticidad">
            <select value={f.criticidad||"media"} onChange={e=>setF({...f,criticidad:e.target.value})} className="w-full border rounded px-2 py-1 bg-white">
              {Object.keys(CRIT).map(v=><option key={v} value={v}>{v}</option>)}
            </select>
          </L>
          <L label="Owner funcional"><input value={f.owner_funcional||""} onChange={e=>setF({...f,owner_funcional:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Owner técnico"><input value={f.owner_tecnico||""} onChange={e=>setF({...f,owner_tecnico:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Proveedor"><input value={f.proveedor||""} onChange={e=>setF({...f,proveedor:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Contrato"><input value={f.contrato||""} onChange={e=>setF({...f,contrato:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Usuarios"><input value={f.usuarios||""} onChange={e=>setF({...f,usuarios:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Licencias"><input value={f.licencias||""} onChange={e=>setF({...f,licencias:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Ubicación"><input value={f.ubicacion||""} onChange={e=>setF({...f,ubicacion:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Modalidad alojamiento"><input value={f.modalidad_alojamiento||""} onChange={e=>setF({...f,modalidad_alojamiento:e.target.value})} className="w-full border rounded px-2 py-1" placeholder="On-prem · Cloud · Hybrid"/></L>
          <L label="Integraciones" span={2}><input value={f.integraciones||""} onChange={e=>setF({...f,integraciones:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Accesos admin" span={2}><input value={f.accesos_admin||""} onChange={e=>setF({...f,accesos_admin:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Backups"><input value={f.backups||""} onChange={e=>setF({...f,backups:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Plan continuidad"><input value={f.plan_continuidad||""} onChange={e=>setF({...f,plan_continuidad:e.target.value})} className="w-full border rounded px-2 py-1"/></L>
          <L label="Notas" span={2}>
            <textarea value={f.notas||""} onChange={e=>setF({...f,notas:e.target.value})} rows={3} className="w-full border rounded px-2 py-1"/>
          </L>
        </div>
        <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
          <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded" data-testid="intit-item-save">
            {saving ? "…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function L({ label, children, span }) {
  return (
    <label className={`block ${span===2?"col-span-2":""}`}>
      <span className="text-[9px] uppercase text-gray-500 font-semibold">{label}</span>
      {children}
    </label>
  );
}


// ════════════════════════════════════════════════════════════════════════════
// DashboardOverview: KPIs globales y cumplimiento por template / criticidad
// ════════════════════════════════════════════════════════════════════════════
function DashboardOverview({ dash, delta, loading, onRefresh }) {
  if (loading && !dash) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500 text-xs">
        <Loader2 className="animate-spin inline mr-1" size={14}/> Calculando KPIs…
      </div>
    );
  }
  if (!dash) return null;
  const { kpis, by_template, by_criticality, top_pending_critical, per_checklist, timeseries } = dash;
  const hasDelta = delta && delta.has_previous;

  const critBadge = {
    alta: "bg-orange-100 text-orange-700 border-orange-200",
    media: "bg-amber-100 text-amber-700 border-amber-200",
    baja: "bg-gray-100 text-gray-600 border-gray-200",
    "crítica": "bg-rose-100 text-rose-700 border-rose-200",
    critica: "bg-rose-100 text-rose-700 border-rose-200",
  };

  const maxTs = Math.max(1, ...timeseries.map(t => t.completed));

  return (
    <div className="space-y-3" data-testid="intit-dashboard">
      {hasDelta && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="intit-delta-banner">
          <div className="flex items-center gap-2 text-[11px] text-indigo-900">
            <BarChart3 size={13} className="text-indigo-600"/>
            <span className="font-semibold">Comparativa vs mes anterior</span>
            <span className="text-indigo-500">({delta.previous_period})</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] flex-wrap">
            <DeltaPill label="Global" value={delta.pct_global_delta} suffix="pp"/>
            <DeltaPill label="Completados" value={delta.completed_delta}/>
            <DeltaPill label="Críticos" value={delta.critical_pending_delta} lowerIsBetter/>
            {delta.new_critical_count > 0 && (
              <span className="bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-bold">
                +{delta.new_critical_count} nuevos críticos
              </span>
            )}
            {delta.resolved_critical_count > 0 && (
              <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">
                ✓ {delta.resolved_critical_count} resueltos
              </span>
            )}
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KpiCard label="Cumplimiento global" value={`${kpis.pct_global}%`} sub={`${kpis.completed_items}/${kpis.total_items} items`} accent="emerald" testid="kpi-pct-global" delta={hasDelta ? delta.pct_global_delta : null} deltaSuffix="pp"/>
        <KpiCard label="Checklists activos" value={kpis.checklists_count} sub="onboarding en curso" accent="blue" testid="kpi-checklists" delta={hasDelta ? delta.checklists_count_delta : null}/>
        <KpiCard label="Items totales" value={kpis.total_items} sub="todas las plantillas" accent="slate" testid="kpi-total-items" delta={hasDelta ? delta.total_items_delta : null}/>
        <KpiCard label="Items completados" value={kpis.completed_items} sub="OK · N/A · completado" accent="green" testid="kpi-completed" delta={hasDelta ? delta.completed_delta : null}/>
        <KpiCard label="Críticos pendientes" value={kpis.critical_pending} sub="prioridad alta" accent="rose" testid="kpi-critical" delta={hasDelta ? delta.critical_pending_delta : null} deltaLowerIsBetter/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* % por template */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 lg:col-span-1">
          <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5"><BarChart3 size={12}/> Cumplimiento por plantilla</h3>
          {by_template.length === 0 && <p className="text-[10px] text-gray-400">Sin datos</p>}
          <div className="space-y-2">
            {by_template.map(t => (
              <div key={t.id}>
                <div className="flex items-center justify-between text-[10px] mb-0.5">
                  <span className="text-gray-700 font-medium truncate">{t.label}</span>
                  <span className="font-bold text-gray-900">{t.pct}% <span className="text-gray-400 font-normal">({t.completed}/{t.total})</span></span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#007934] rounded-full transition-all" style={{ width: `${t.pct}%` }}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* % por criticidad */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 lg:col-span-1">
          <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5"><AlertTriangle size={12}/> Cumplimiento por criticidad</h3>
          {by_criticality.length === 0 && <p className="text-[10px] text-gray-400">Sin datos</p>}
          <div className="space-y-2">
            {by_criticality.map(c => (
              <div key={c.crit}>
                <div className="flex items-center justify-between text-[10px] mb-0.5">
                  <span className={`inline-flex px-1.5 py-0.5 rounded border font-semibold ${critBadge[c.crit] || critBadge.baja}`}>{c.crit}</span>
                  <span className="font-bold text-gray-900">{c.pct}% <span className="text-gray-400 font-normal">({c.completed}/{c.total})</span></span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${c.crit === 'alta' || c.crit === 'crítica' || c.crit === 'critica' ? 'bg-rose-500' : c.crit === 'media' ? 'bg-amber-500' : 'bg-gray-400'}`} style={{ width: `${c.pct}%` }}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeseries 8 semanas */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 lg:col-span-1">
          <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5"><BarChart3 size={12}/> Items completados · últimas 8 semanas</h3>
          <div className="flex items-end gap-1 h-24 mt-2">
            {timeseries.map((t, i) => (
              <div key={i} className="flex-1 flex flex-col items-center" title={`${t.label}: ${t.completed} items`}>
                <div
                  className="w-full bg-[#007934] rounded-t transition-all hover:opacity-80"
                  style={{ height: `${Math.max(2, (t.completed / maxTs) * 100)}%`, minHeight: 2 }}
                />
                <div className="text-[8px] text-gray-400 mt-1">{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top pendientes críticos + Per checklist */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-800 flex items-center gap-1.5"><AlertTriangle size={12} className="text-rose-500"/> Top pendientes críticos</h3>
            <button onClick={onRefresh} className="text-[10px] text-gray-400 hover:text-gray-700">↻ refrescar</button>
          </div>
          {top_pending_critical.length === 0 && <p className="text-[10px] text-gray-400">¡Sin pendientes críticos!</p>}
          <div className="space-y-1 max-h-48 overflow-auto">
            {top_pending_critical.map((p, i) => (
              <div key={i} className="text-[10px] border-b border-gray-100 py-1.5 last:border-0">
                <div className="font-semibold text-gray-800 truncate">{p.titulo || "(sin título)"}</div>
                <div className="text-gray-500 flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{p.checklist_name}</span>
                  <span>·</span>
                  <span>{p.template}</span>
                  {p.categoria && <><span>·</span><span className="truncate">{p.categoria}</span></>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5"><FileCheck size={12}/> Progreso por checklist</h3>
          {per_checklist.length === 0 && <p className="text-[10px] text-gray-400">Sin checklists</p>}
          <div className="space-y-1.5 max-h-48 overflow-auto">
            {per_checklist.map(c => (
              <div key={c.id}>
                <div className="flex items-center justify-between text-[10px] mb-0.5">
                  <span className="text-gray-700 font-medium truncate">{c.name}</span>
                  <span className="font-bold text-gray-900">{c.pct}% <span className="text-gray-400 font-normal">({c.completed}/{c.total})</span></span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${c.pct}%` }}/>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent = "slate", testid, delta, deltaSuffix = "", deltaLowerIsBetter = false }) {
  const accents = {
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-800",
    blue:    "from-blue-50 to-blue-100 border-blue-200 text-blue-800",
    slate:   "from-slate-50 to-slate-100 border-slate-200 text-slate-800",
    green:   "from-green-50 to-green-100 border-green-200 text-green-800",
    rose:    "from-rose-50 to-rose-100 border-rose-200 text-rose-800",
  };
  let deltaBadge = null;
  if (delta !== null && delta !== undefined) {
    const isZero = delta === 0;
    const improved = (delta > 0 && !deltaLowerIsBetter) || (delta < 0 && deltaLowerIsBetter);
    const colorCls = isZero ? "bg-gray-100 text-gray-500"
      : (improved ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700");
    const arrow = isZero ? "→" : (delta > 0 ? "▲" : "▼");
    const sign = delta > 0 ? "+" : "";
    deltaBadge = (
      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${colorCls}`} title="vs mes anterior">
        {arrow} {sign}{delta}{deltaSuffix}
      </span>
    );
  }
  return (
    <div className={`bg-gradient-to-br ${accents[accent]} border rounded-xl p-3 relative`} data-testid={testid}>
      {deltaBadge && <div className="absolute top-2 right-2">{deltaBadge}</div>}
      <div className="text-[9px] uppercase font-bold tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-0.5">{value}</div>
      <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}

function DeltaPill({ label, value, suffix = "", lowerIsBetter = false }) {
  const v = Number(value || 0);
  const isZero = v === 0;
  const improved = (v > 0 && !lowerIsBetter) || (v < 0 && lowerIsBetter);
  const cls = isZero ? "bg-gray-100 text-gray-600 border-gray-200"
    : improved ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : "bg-rose-100 text-rose-700 border-rose-200";
  const arrow = isZero ? "→" : (v > 0 ? "▲" : "▼");
  const sign = v > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-semibold ${cls}`}>
      <span className="opacity-70">{label}:</span> {arrow} {sign}{v}{suffix}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CustomFieldsEditor: CRUD de campos extra + overrides por template
// ════════════════════════════════════════════════════════════════════════════
const TEMPLATE_OPTS = [
  { id: "it", label: "IT / Sistemas" },
  { id: "rrhh", label: "RRHH" },
  { id: "finanzas", label: "Finanzas" },
  { id: "juridico", label: "Jurídico" },
  { id: "compliance", label: "Compliance" },
  { id: "produccion", label: "Producción" },
];

const FIELD_TYPES = [
  { id: "text", label: "Texto" },
  { id: "textarea", label: "Texto largo" },
  { id: "select", label: "Lista (select)" },
  { id: "date", label: "Fecha" },
  { id: "bool", label: "Sí/No" },
  { id: "number", label: "Número" },
];

function CustomFieldsEditor({ onClose }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null = nuevo modal cerrado; {} = nuevo; {id..} = editar
  const [filterTemplate, setFilterTemplate] = useState("");

  const load = async () => {
    const r = await axios.get(`${API}/integration/form-extras`);
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const remove = async (xid) => {
    if (!window.confirm("¿Eliminar este campo? (No afecta a checklists ya creados)")) return;
    try {
      await axios.delete(`${API}/integration/form-extras/${xid}`);
      toast.success("Campo eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  const filtered = (items || []).filter(it => !filterTemplate || it.template_id === filterTemplate);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-2 md:p-4 overflow-auto" data-testid="intit-fields-editor">
      <div className="bg-white rounded-xl w-full max-w-5xl shadow-2xl my-4">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Settings2 size={14}/> Editor global de campos · Integración IT</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Añade campos extra, modifica los base o ocúltalos. Los cambios solo afectan a checklists nuevos.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X size={16}/></button>
        </div>

        <div className="p-3 flex items-center gap-2 flex-wrap border-b">
          <select
            value={filterTemplate}
            onChange={e => setFilterTemplate(e.target.value)}
            className="text-xs border border-gray-200 rounded px-2 py-1"
            data-testid="intit-fe-filter-template">
            <option value="">Todas las plantillas</option>
            {TEMPLATE_OPTS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <span className="text-[10px] text-gray-500">{filtered.length} campos</span>
          <div className="flex-1"/>
          <button
            onClick={() => setEditing({})}
            className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1"
            data-testid="intit-fe-new-btn">
            <Plus size={11}/> Nuevo campo
          </button>
        </div>

        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          {items === null ? (
            <div className="text-center text-xs text-gray-500 p-6"><Loader2 className="inline animate-spin mr-1" size={12}/> Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-xs text-gray-500 p-6">No hay campos extra todavía. Pulsa &quot;Nuevo campo&quot; para añadir el primero.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left p-2 font-semibold">Plantilla</th>
                  <th className="text-left p-2 font-semibold">Categoría</th>
                  <th className="text-left p-2 font-semibold">Subcategoría</th>
                  <th className="text-left p-2 font-semibold">Título</th>
                  <th className="text-left p-2 font-semibold">Tipo</th>
                  <th className="text-left p-2 font-semibold">Críticidad</th>
                  <th className="text-left p-2 font-semibold">Comportamiento</th>
                  <th className="text-right p-2 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => (
                  <tr key={it.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`intit-fe-row-${it.id}`}>
                    <td className="p-2 font-semibold text-gray-700">{TEMPLATE_OPTS.find(t=>t.id===it.template_id)?.label || it.template_id}</td>
                    <td className="p-2 text-gray-600 truncate max-w-[140px]" title={it.cat}>{it.cat}</td>
                    <td className="p-2 text-gray-600 truncate max-w-[120px]" title={it.sub}>{it.sub}</td>
                    <td className="p-2 text-gray-900 font-medium truncate max-w-[200px]" title={it.title}>{it.title}</td>
                    <td className="p-2 text-gray-600">{FIELD_TYPES.find(f=>f.id===it.field_type)?.label || it.field_type}</td>
                    <td className="p-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${CRIT[it.crit] || CRIT.baja}`}>{it.crit}</span></td>
                    <td className="p-2 text-[10px]">
                      {it.hidden && <span className="inline-flex items-center gap-0.5 bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded"><EyeOff size={9}/> oculto</span>}
                      {!it.hidden && it.overrides_base_key && <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">override</span>}
                      {!it.hidden && !it.overrides_base_key && <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">extra</span>}
                      {it.required && <span className="ml-1 text-rose-600 font-bold">*</span>}
                    </td>
                    <td className="p-2 text-right">
                      <button onClick={() => setEditing(it)} className="text-blue-600 hover:text-blue-800 p-1" title="Editar" data-testid={`intit-fe-edit-${it.id}`}><Edit3 size={11}/></button>
                      <button onClick={() => remove(it.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Eliminar" data-testid={`intit-fe-del-${it.id}`}><Trash2 size={11}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {editing !== null && (
          <FieldExtraForm
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </div>
    </div>
  );
}

function FieldExtraForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({
    template_id: initial.template_id || "it",
    cat: initial.cat || "",
    sub: initial.sub || "",
    title: initial.title || "",
    desc: initial.desc || "",
    field_type: initial.field_type || "text",
    crit: initial.crit || "media",
    required: !!initial.required,
    hidden: !!initial.hidden,
    overrides_base_key: initial.overrides_base_key || "",
    options: (initial.options || []).join(", "),
    orden: initial.orden || 999,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.cat.trim() || !f.sub.trim() || !f.title.trim()) {
      toast.error("Categoría, subcategoría y título son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        options: f.options ? f.options.split(",").map(s => s.trim()).filter(Boolean) : [],
        orden: parseInt(f.orden) || 999,
      };
      if (isEdit) {
        await axios.put(`${API}/integration/form-extras/${initial.id}`, payload);
        toast.success("Campo actualizado");
      } else {
        await axios.post(`${API}/integration/form-extras`, payload);
        toast.success("Campo creado");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-2 md:p-4 overflow-auto" data-testid="intit-fe-form">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl my-4">
        <div className="flex items-center justify-between p-3 border-b">
          <h4 className="text-sm font-bold text-gray-900">{isEdit ? "Editar campo" : "Nuevo campo"}</h4>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X size={14}/></button>
        </div>
        <div className="p-3 grid grid-cols-2 gap-2 text-xs">
          <L label="Plantilla">
            <select value={f.template_id} onChange={e=>setF({...f,template_id:e.target.value})} className="w-full border rounded px-2 py-1" data-testid="intit-fe-template">
              {TEMPLATE_OPTS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </L>
          <L label="Tipo de campo">
            <select value={f.field_type} onChange={e=>setF({...f,field_type:e.target.value})} className="w-full border rounded px-2 py-1" data-testid="intit-fe-type">
              {FIELD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </L>
          <L label="Categoría">
            <input value={f.cat} onChange={e=>setF({...f,cat:e.target.value})} placeholder="Ej. A · Sistemas y aplicaciones" className="w-full border rounded px-2 py-1" data-testid="intit-fe-cat"/>
          </L>
          <L label="Subcategoría">
            <input value={f.sub} onChange={e=>setF({...f,sub:e.target.value})} placeholder="Ej. Control horario" className="w-full border rounded px-2 py-1" data-testid="intit-fe-sub"/>
          </L>
          <L label="Título" span={2}>
            <input value={f.title} onChange={e=>setF({...f,title:e.target.value})} placeholder="Pregunta o ítem a verificar" className="w-full border rounded px-2 py-1" data-testid="intit-fe-title"/>
          </L>
          <L label="Descripción" span={2}>
            <textarea value={f.desc} onChange={e=>setF({...f,desc:e.target.value})} rows={2} placeholder="Detalle / contexto" className="w-full border rounded px-2 py-1" data-testid="intit-fe-desc"/>
          </L>
          <L label="Criticidad">
            <select value={f.crit} onChange={e=>setF({...f,crit:e.target.value})} className="w-full border rounded px-2 py-1" data-testid="intit-fe-crit">
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
            </select>
          </L>
          <L label="Orden">
            <input type="number" value={f.orden} onChange={e=>setF({...f,orden:e.target.value})} className="w-full border rounded px-2 py-1"/>
          </L>
          {f.field_type === "select" && (
            <L label="Opciones (separadas por coma)" span={2}>
              <input value={f.options} onChange={e=>setF({...f,options:e.target.value})} placeholder="Opción 1, Opción 2, Opción 3" className="w-full border rounded px-2 py-1"/>
            </L>
          )}
          <L label="Sustituye item base (opcional · tid:cat:sub:title)" span={2}>
            <input value={f.overrides_base_key} onChange={e=>setF({...f,overrides_base_key:e.target.value})} placeholder="vacío = añade campo nuevo" className="w-full border rounded px-2 py-1 font-mono" data-testid="intit-fe-overrides"/>
          </L>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-700">
            <input type="checkbox" checked={f.required} onChange={e=>setF({...f,required:e.target.checked})}/>
            Obligatorio
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-gray-700">
            <input type="checkbox" checked={f.hidden} onChange={e=>setF({...f,hidden:e.target.checked})}/>
            Ocultar item base (requiere clave de sustitución)
          </label>
        </div>
        <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
          <button onClick={onClose} className="text-xs border px-3 py-1.5 rounded">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-[#007934] text-white px-3 py-1.5 rounded font-semibold" data-testid="intit-fe-save">
            {saving ? "…" : (isEdit ? "Guardar cambios" : "Crear campo")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// EmailSettingsModal · Configura destinatarios y envía snapshot mensual
// ════════════════════════════════════════════════════════════════════════════
function EmailSettingsModal({ onClose }) {
  const [s, setS] = useState(null);
  const [recInput, setRecInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/integration/dashboard/email-settings`);
      setS(r.data);
      setRecInput((r.data.recipients || []).join("\n"));
    } catch (e) {
      toast.error("No se pudieron cargar los ajustes");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = recInput
        .split(/[\n,;]/).map(x => x.trim()).filter(Boolean);
      const payload = {
        enabled: s.enabled,
        recipients,
        subject: s.subject,
      };
      await axios.put(`${API}/integration/dashboard/email-settings`, payload);
      toast.success("Ajustes guardados");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("¿Enviar el snapshot ahora a todos los destinatarios?")) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/integration/dashboard/send-now`);
      if (r.data.ok) {
        toast.success(`Enviado a ${r.data.sent_to.length} destinatario(s)`);
        load();
      } else {
        toast.error("No hay destinatarios configurados");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error enviando");
    } finally {
      setSending(false);
    }
  };

  if (!s) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded-xl p-6 text-xs text-gray-500"><Loader2 className="inline animate-spin mr-1" size={12}/> Cargando…</div>
      </div>
    );
  }

  const validCount = recInput.split(/[\n,;]/).map(x => x.trim()).filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-2 md:p-4 overflow-auto" data-testid="intit-email-settings">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-2xl my-4">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Mail size={14}/> Reporte mensual · Comité de Integración</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">El sistema envía automáticamente el snapshot del dashboard (PDF) el día 1 de cada mes a las 08:00 UTC.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1"><X size={16}/></button>
        </div>

        <div className="p-4 space-y-3 text-xs">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={s.enabled}
              onChange={e => setS({...s, enabled: e.target.checked})}
              className="w-4 h-4"
              data-testid="intit-email-enabled"/>
            <span className="font-semibold text-gray-800">Envío mensual automático activado</span>
            <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold ${s.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
              {s.enabled ? "ACTIVO" : "PAUSADO"}
            </span>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Asunto del email</span>
            <input
              value={s.subject || ""}
              onChange={e => setS({...s, subject: e.target.value})}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 mt-1"
              data-testid="intit-email-subject"/>
          </label>

          <label className="block">
            <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-wide">Destinatarios (uno por línea o separados por coma)</span>
            <textarea
              value={recInput}
              onChange={e => setRecInput(e.target.value)}
              rows={5}
              placeholder="comite-integracion@valorizasm.com&#10;cfo@valorizasm.com"
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 mt-1 font-mono text-[11px]"
              data-testid="intit-email-recipients"/>
          </label>
          <p className="text-[10px] text-gray-500 -mt-2">
            {validCount} email(s) detectados
          </p>

          {s.last_sent && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-[11px] text-gray-700">
              <div className="flex items-center gap-1.5">
                <Calendar size={11} className="text-gray-400"/>
                <span>Último envío: <span className="font-semibold">{new Date(s.last_sent).toLocaleString()}</span></span>
              </div>
              {s.last_status && (
                <div className="text-[10px] text-gray-500 mt-0.5 ml-4">
                  Disparado por: {s.last_status.triggered_by} · {s.last_status.sent} enviados · {s.last_status.errors} errores
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-[11px] text-blue-800">
            <strong>Programación:</strong> Día 1 de cada mes a las 08:00 UTC. El PDF incluye KPIs globales, cumplimiento por plantilla y criticidad, evolución 8 semanas, progreso por checklist y top pendientes críticos.
          </div>
        </div>

        <div className="flex justify-between gap-2 p-3 border-t bg-gray-50">
          <button
            onClick={sendNow}
            disabled={sending}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded font-semibold flex items-center gap-1 disabled:opacity-50"
            data-testid="intit-email-send-now">
            {sending ? <Loader2 className="animate-spin" size={11}/> : <Send size={11}/>}
            Enviar prueba ahora
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-xs border border-gray-200 px-3 py-1.5 rounded">Cerrar</button>
            <button
              onClick={save}
              disabled={saving}
              className="text-xs bg-[#007934] hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-semibold disabled:opacity-50"
              data-testid="intit-email-save">
              {saving ? "…" : "Guardar ajustes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
